import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../../store/auth'
import { useUpdateMe, useLogout } from '../../../hooks/useAuth'
import { useUpdateNotificationPreference } from '../../../hooks/useNotifications'
import { ProfileForm, type ProfileFormValues } from '../ProfileForm'
import { SettingsGroup } from '../SettingsGroup'
import { SettingsToggleRow } from '../SettingsToggleRow'

const ACCOUNT_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '15px 0',
}

export function SettingsTab() {
  const { user } = useAuthStore()
  const updateMe = useUpdateMe()
  const updateNotifPref = useUpdateNotificationPreference()
  const logout = useLogout()

  const [saved, setSaved] = useState(false)

  // Notification + privacy prefs (optimistic local mirror of the persisted user).
  const [notifyNewEvents, setNotifyNewEvents] = useState(user?.notifyNewEvents ?? true)
  const [notifyRsvpReminders, setNotifyRsvpReminders] = useState(user?.notifyRsvpReminders ?? true)
  const [profilePublic, setProfilePublic] = useState(user?.profilePublic ?? true)
  const [privacyShowLastName, setPrivacyShowLastName] = useState(user?.privacyShowLastName ?? true)
  const [privacyRsvpPublic, setPrivacyRsvpPublic] = useState(user?.privacyRsvpPublic ?? true)

  useEffect(() => {
    if (!user) return
    setNotifyNewEvents(user.notifyNewEvents ?? true)
    setNotifyRsvpReminders(user.notifyRsvpReminders ?? true)
    setProfilePublic(user.profilePublic ?? true)
    setPrivacyShowLastName(user.privacyShowLastName ?? true)
    setPrivacyRsvpPublic(user.privacyRsvpPublic ?? true)
  }, [user])

  if (!user) return null

  function handleSave(values: ProfileFormValues) {
    setSaved(false)
    updateMe.mutate(values, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000) } })
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <ProfileForm user={user} onSave={handleSave} saving={updateMe.isPending} saved={saved} />

      {/* Notifications */}
      <SettingsGroup label="Notifications">
        <SettingsToggleRow
          label="New event emails"
          description="When churches you follow post events"
          checked={notifyNewEvents}
          onChange={(v) => { setNotifyNewEvents(v); updateNotifPref.mutate(v) }}
        />
        <SettingsToggleRow
          label="RSVP reminders"
          description="A nudge the day before an event you're going to"
          checked={notifyRsvpReminders}
          onChange={(v) => { setNotifyRsvpReminders(v); updateMe.mutate({ notifyRsvpReminders: v }) }}
          last
        />
      </SettingsGroup>

      {/* Privacy */}
      <SettingsGroup label="Privacy">
        <SettingsToggleRow
          label="Public profile"
          description={profilePublic
            ? 'Others can view your full host profile'
            : 'Profile page hidden — but your name & photo still show on events you host'}
          checked={profilePublic}
          onChange={(v) => { setProfilePublic(v); updateMe.mutate({ profilePublic: v }) }}
        />
        <SettingsToggleRow
          label="Show full name"
          description="Full name visible to hosts"
          checked={privacyShowLastName}
          onChange={(v) => { setPrivacyShowLastName(v); updateMe.mutate({ privacyShowLastName: v }) }}
        />
        <SettingsToggleRow
          label="Public RSVP history"
          description="Others can see events you attend"
          checked={privacyRsvpPublic}
          onChange={(v) => { setPrivacyRsvpPublic(v); updateMe.mutate({ privacyRsvpPublic: v }) }}
          last
        />
      </SettingsGroup>

      {/* Account */}
      <SettingsGroup label="Account">
        <div style={{ ...ACCOUNT_ROW, borderBottom: '1px solid var(--line-soft)' }}>
          <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>Email</span>
          <span style={{ fontSize: 13.5, color: 'var(--muted)' }}>{user.email}</span>
        </div>
        <div style={{ ...ACCOUNT_ROW, borderBottom: '1px solid var(--line-soft)' }}>
          <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>Password</span>
          <Link to="/forgot-password" style={{ fontSize: 13.5, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
            Change password
          </Link>
        </div>
        <div style={ACCOUNT_ROW}>
          <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>Sign out of Fellowship</span>
          <button
            type="button"
            onClick={() => logout.mutate()}
            style={{ height: 34, padding: '0 16px', borderRadius: 999, background: 'transparent', border: '1.5px solid var(--line)', fontSize: 12.5, fontWeight: 500, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Sign out
          </button>
        </div>
      </SettingsGroup>
    </div>
  )
}
